import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { api, ApiRequestError } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { errorText } from '../../../lib/crm';
import { Button, Card, ErrorState, Field, Input, Modal, Skeleton, StatusBadge, cx, useToast } from '../../../components/ui';

type Target =
  | { kind: 'category'; mode: 'add'; parentId?: string; parentName?: string }
  | { kind: 'category'; mode: 'edit'; item: any }
  | { kind: 'brand'; mode: 'add'; categoryId: string; categoryName: string }
  | { kind: 'brand'; mode: 'edit'; item: any };

export function CategoriesTab() {
  const { can } = useAuth();
  const manage = can('crm.catalog.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const [target, setTarget] = useState<Target | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<ApiRequestError | null>(null);

  const q = useQuery({ queryKey: ['crm', 'catalog', 'categories'], queryFn: () => api.get('/crm/categories') });

  const open = (t: Target) => {
    setTarget(t); setError(null);
    setName(t.mode === 'edit' ? t.item.name : '');
  };

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm', 'catalog'] }); };

  const save = useMutation({
    mutationFn: () => {
      const t = target!;
      if (t.kind === 'category') {
        return t.mode === 'add'
          ? api.post('/crm/categories', { name, parentId: t.parentId ?? null })
          : api.patch(`/crm/categories/${t.item.id}`, { name });
      }
      return t.mode === 'add'
        ? api.post('/crm/brands', { categoryId: t.categoryId, name })
        : api.patch(`/crm/brands/${t.item.id}`, { name });
    },
    onSuccess: () => { toast.success('Saved'); setTarget(null); refresh(); },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); toast.error('Could not save', errorText(e)); },
  });

  const toggle = useMutation({
    mutationFn: (v: { kind: 'category' | 'brand'; id: string; active: boolean }) =>
      api.patch(`/crm/${v.kind === 'category' ? 'categories' : 'brands'}/${v.id}`, { active: v.active }),
    onSuccess: refresh,
    onError: (e) => toast.error('Could not update', errorText(e)),
  });

  if (q.isLoading) return <Card><Skeleton className="h-40 w-full" /></Card>;
  if (q.error) return <Card padded={false}><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;

  const title = !target ? '' :
    `${target.mode === 'add' ? 'Add' : 'Rename'} ${target.kind === 'brand' ? 'brand' : (target.kind === 'category' && target.mode === 'add' && target.parentId ? 'subcategory' : 'category')}`;

  return (
    <div className="space-y-4">
      {manage && (
        <div className="flex justify-end">
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => open({ kind: 'category', mode: 'add' })}>Add category</Button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {(q.data?.data ?? []).map((c: any) => (
          <Card key={c.id} className={cx(!c.active && 'opacity-60')}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 text-base font-semibold text-ink-900">
                  {c.name}{!c.active && <StatusBadge>Inactive</StatusBadge>}
                </h3>
                <p className="text-xs text-ink-500">{c.productCount} product{c.productCount === 1 ? '' : 's'}</p>
              </div>
              {manage && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => open({ kind: 'category', mode: 'edit', item: c })}>Rename</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate({ kind: 'category', id: c.id, active: !c.active })}>
                    {c.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Subcategories</p>
                  {manage && <button className="text-xs font-medium text-brand-600 hover:underline"
                    onClick={() => open({ kind: 'category', mode: 'add', parentId: c.id, parentName: c.name })}>+ Add</button>}
                </div>
                <ul className="space-y-1">
                  {c.subcategories.length === 0 && <li className="text-xs text-ink-400">None</li>}
                  {c.subcategories.map((s: any) => (
                    <li key={s.id} className={cx('flex items-center justify-between gap-2 text-sm text-ink-800', !s.active && 'opacity-50')}>
                      <span>{s.name} <span className="text-xs text-ink-400">({s.productCount})</span></span>
                      {manage && (
                        <span className="flex gap-2 text-xs">
                          <button className="text-ink-500 hover:text-ink-900" onClick={() => open({ kind: 'category', mode: 'edit', item: s })}>Rename</button>
                          <button className="text-ink-500 hover:text-ink-900" onClick={() => toggle.mutate({ kind: 'category', id: s.id, active: !s.active })}>{s.active ? 'Hide' : 'Show'}</button>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Brands</p>
                  {manage && <button className="text-xs font-medium text-brand-600 hover:underline"
                    onClick={() => open({ kind: 'brand', mode: 'add', categoryId: c.id, categoryName: c.name })}>+ Add</button>}
                </div>
                <ul className="space-y-1">
                  {c.brands.length === 0 && <li className="text-xs text-ink-400">None</li>}
                  {c.brands.map((b: any) => (
                    <li key={b.id} className={cx('flex items-center justify-between gap-2 text-sm text-ink-800', !b.active && 'opacity-50')}>
                      <span>{b.name} <span className="text-xs text-ink-400">({b.productCount})</span></span>
                      {manage && (
                        <span className="flex gap-2 text-xs">
                          <button className="text-ink-500 hover:text-ink-900" onClick={() => open({ kind: 'brand', mode: 'edit', item: b })}>Rename</button>
                          <button className="text-ink-500 hover:text-ink-900" onClick={() => toggle.mutate({ kind: 'brand', id: b.id, active: !b.active })}>{b.active ? 'Hide' : 'Show'}</button>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!target} onClose={() => setTarget(null)} title={title} size="sm"
        description={target && target.mode === 'add'
          ? (target.kind === 'brand' ? `Under ${target.categoryName}` : target.parentName ? `Under ${target.parentName}` : undefined) : undefined}
        footer={<>
          <Button onClick={() => setTarget(null)}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={name.trim().length < 2} onClick={() => save.mutate()}>Save</Button>
        </>}>
        <Field label="Name" required error={error?.fieldError('name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={80} invalid={!!error?.fieldError('name')} />
        </Field>
      </Modal>
    </div>
  );
}
